import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, notifyError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  History as HistoryIcon,
  ArrowDownToLine,
  ArrowUpFromLine,
  Loader2,
  Search,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";

const PAGE_SIZE = 20;

export default function History() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dfrom, setDfrom] = useState("");
  const [dto, setDto] = useState("");

  const load = useCallback(
    async (targetOffset = 0, overrides = {}) => {
      const qv = overrides.q ?? q;
      const dfromV = overrides.dfrom ?? dfrom;
      const dtoV = overrides.dto ?? dto;
      setLoading(true);
      try {
        const p = new URLSearchParams();
        if (qv.trim()) p.set("q", qv.trim());
        if (dfromV) p.set("dfrom", dfromV);
        if (dtoV) p.set("dto", `${dtoV}T23:59:59`);
        p.set("limit", String(PAGE_SIZE));
        p.set("offset", String(targetOffset));
        const { data } = await api.get(`/movements?${p.toString()}`);
        setItems(data.items || []);
        setTotal(data.total || 0);
        setOffset(targetOffset);
      } catch (e) {
        notifyError(e);
      } finally {
        setLoading(false);
      }
    },
    [q, dfrom, dto]
  );

  useEffect(() => {
    load(0);
    // eslint-disable-next-line
  }, []);

  const applyFilters = (e) => {
    e?.preventDefault();
    load(0);
  };

  const clearFilters = () => {
    setQ("");
    setDfrom("");
    setDto("");
    load(0, { q: "", dfrom: "", dto: "" });
  };

  const hasFilters = q.trim() || dfrom || dto;
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + PAGE_SIZE, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto space-y-6" data-testid="history-page">
      <div>
        <div className="label-eyebrow text-amber-600">Hareketler</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">Geçmiş</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Emanet Lastiklere konu olan tüm giriş ve teslim hareketleri.
        </p>
      </div>

      {/* Filters */}
      <Card className="rounded-sm border-border p-4">
        <form onSubmit={applyFilters} className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px] space-y-1.5">
            <Label className="label-eyebrow">Müşteri Ara</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Ad / soyad veya telefon..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-10 h-10 rounded-sm bg-card"
                data-testid="history-search"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Başlangıç</Label>
            <Input
              type="date"
              value={dfrom}
              onChange={(e) => setDfrom(e.target.value)}
              className="rounded-sm h-10 w-40"
              data-testid="history-from"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Bitiş</Label>
            <Input
              type="date"
              value={dto}
              onChange={(e) => setDto(e.target.value)}
              className="rounded-sm h-10 w-40"
              data-testid="history-to"
            />
          </div>
          <Button
            type="submit"
            className="rounded-sm bg-foreground text-background hover:bg-foreground/90 h-10"
            data-testid="history-apply"
          >
            Uygula
          </Button>
          {hasFilters && (
            <Button
              type="button"
              variant="outline"
              onClick={clearFilters}
              className="rounded-sm h-10"
              data-testid="history-clear"
            >
              <X className="h-4 w-4 mr-1" /> Temizle
            </Button>
          )}
        </form>
      </Card>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center rounded-sm">
          <HistoryIcon className="h-10 w-10 mx-auto text-muted-foreground" />
          <div className="font-display font-bold text-lg mt-3">
            {hasFilters ? "Sonuç bulunamadı" : "Henüz hareket yok"}
          </div>
        </Card>
      ) : (
        <>
          <div className="relative pl-8">
            <div className="absolute left-3 top-2 bottom-2 w-px bg-border" />
            {items.map((m) => (
              <Link
                key={m.id}
                to={`/emanetler/${m.storage_id}`}
                className="relative block mb-4 group"
                data-testid={`movement-${m.id}`}
              >
                <div
                  className={`absolute -left-6 top-3 h-6 w-6 rounded-full flex items-center justify-center border-2 ${
                    m.type === "giris"
                      ? "bg-amber-500 text-foreground border-amber-500"
                      : "bg-foreground text-background border-foreground"
                  }`}
                >
                  {m.type === "giris" ? <ArrowDownToLine className="h-3 w-3" /> : <ArrowUpFromLine className="h-3 w-3" />}
                </div>
                <Card className="surface-card p-4 rounded-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge
                          className={`rounded-sm ${
                            m.type === "giris"
                              ? "bg-amber-500 text-white hover:bg-amber-500"
                              : "bg-foreground text-background hover:bg-foreground"
                          }`}
                        >
                          {m.type === "giris" ? "GİRİŞ" : "ÇIKIŞ"}
                        </Badge>
                        <span className="font-semibold text-sm truncate">{m.customer_name || "—"}</span>
                      </div>
                      {m.notes && <div className="text-xs text-muted-foreground mt-2">{m.notes}</div>}
                    </div>
                    <div className="text-xs font-mono text-muted-foreground whitespace-nowrap">
                      {new Date(m.date).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })}
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between gap-3 pt-2">
            <div className="text-xs text-muted-foreground font-mono" data-testid="history-range">
              {from}–{to} / {total}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="rounded-sm"
                onClick={() => load(Math.max(0, offset - PAGE_SIZE))}
                disabled={!canPrev}
                data-testid="history-prev"
              >
                <ChevronLeft className="h-4 w-4" /> Önceki
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="rounded-sm"
                onClick={() => load(offset + PAGE_SIZE)}
                disabled={!canNext}
                data-testid="history-next"
              >
                Sonraki <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
